// frontend/src/components/CrearEditarDocumentoLegalModal.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { FiPlus, FiTrash2, FiPaperclip, FiX, FiFileText, FiCalendar, FiHash, FiUser, FiFolder, FiShoppingCart, FiAlignLeft, FiPackage, FiDollarSign, FiLayers } from 'react-icons/fi';
import './CrearModal.css';
import './CrearEditarDocumentoLegalModal.css';

const TIPO_DOCUMENTO_CHOICES = [
  { value: 'factura_compra', label: 'Factura de Compra', icon: '📥', color: '#f59e0b' },
  { value: 'factura_venta', label: 'Factura de Venta', icon: '📤', color: '#10b981' },
  { value: 'boleta', label: 'Boleta', icon: '🧾', color: '#3b82f6' },
  { value: 'nota_credito', label: 'Nota de Crédito', icon: '📋', color: '#ef4444' },
  { value: 'nota_debito', label: 'Nota de Débito', icon: '📝', color: '#ec4899' },
  { value: 'guia_despacho', label: 'Guía de Despacho', icon: '🚚', color: '#8b5cf6' },
  { value: 'factura_exenta', label: 'Factura Exenta', icon: '📑', color: '#6366f1' },
  { value: 'boleta_honorarios', label: 'Boleta de Honorarios', icon: '👤', color: '#14b8a6' }
];

const ESTADO_CHOICES = [
  { value: 'borrador', label: 'Borrador', color: '#6b7280' },
  { value: 'emitida', label: 'Emitida', color: '#3b82f6' },
  { value: 'pagada', label: 'Pagada', color: '#10b981' },
  { value: 'vencida', label: 'Vencida', color: '#f59e0b' },
  { value: 'anulada', label: 'Anulada', color: '#ef4444' }
];

const CrearEditarDocumentoLegalModal = ({ onClose, onCreate, documentoEdit = null }) => {
  const [proyectos, setProyectos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [ordenesCompra, setOrdenesCompra] = useState([]);
  const [selectedOrdenCompra, setSelectedOrdenCompra] = useState(null);
  const [recursos, setRecursos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [archivoAdjunto, setArchivoAdjunto] = useState(null);
  const [activeStep, setActiveStep] = useState(0); // 0: info, 1: detalles, 2: resumen

  const [formData, setFormData] = useState({
    tipo_documento: documentoEdit?.tipo_documento || '',
    numero_documento: documentoEdit?.numero_documento || '',
    descripcion: documentoEdit?.descripcion || '',
    fecha_emision: documentoEdit?.fecha_emision || new Date().toISOString().split('T')[0],
    fecha_vencimiento: documentoEdit?.fecha_vencimiento || '',
    proyecto: documentoEdit?.proyecto ? String(documentoEdit.proyecto) : '',
    proveedor: documentoEdit?.proveedor ? String(documentoEdit.proveedor) : '',
    cliente: documentoEdit?.cliente ? String(documentoEdit.cliente) : '',
    estado: documentoEdit?.estado || 'borrador',
    orden_compra: ''
  });

  const [detalles, setDetalles] = useState(
    documentoEdit?.detalles?.length > 0
      ? documentoEdit.detalles.map(d => ({
          descripcion: d.descripcion,
          cantidad: d.cantidad,
          unidad: d.unidad,
          precio_unitario: d.precio_unitario,
          presupuesto_costo_recurso: d.presupuesto_costo_recurso || '',
          recurso_id: d.recurso_referencia_id ? String(d.recurso_referencia_id) : ''
        }))
      : [{ descripcion: '', cantidad: '', unidad: 'unidad', precio_unitario: '', presupuesto_costo_recurso: '', recurso_id: '' }]
  );

  useEffect(() => {
    cargarProyectos();
    cargarProveedores();
    cargarClientes();
    cargarRecursos();
  }, []);

  useEffect(() => {
    if (documentoEdit) {
      setFormData({
        tipo_documento: documentoEdit.tipo_documento || '',
        numero_documento: documentoEdit.numero_documento || '',
        descripcion: documentoEdit.descripcion || '',
        fecha_emision: documentoEdit.fecha_emision || new Date().toISOString().split('T')[0],
        fecha_vencimiento: documentoEdit.fecha_vencimiento || '',
        proyecto: documentoEdit.proyecto ? String(documentoEdit.proyecto) : '',
        proveedor: documentoEdit.proveedor ? String(documentoEdit.proveedor) : '',
        cliente: documentoEdit.cliente ? String(documentoEdit.cliente) : '',
        estado: documentoEdit.estado || 'borrador',
        orden_compra: ''
      });
    }
  }, [documentoEdit]);

  useEffect(() => {
    if (documentoEdit?.detalles?.length > 0) {
      setDetalles(documentoEdit.detalles.map(d => ({
        descripcion: d.descripcion,
        cantidad: d.cantidad,
        unidad: d.unidad,
        precio_unitario: d.precio_unitario,
        presupuesto_costo_recurso: d.presupuesto_costo_recurso || '',
        recurso_id: d.recurso_referencia_id ? String(d.recurso_referencia_id) : ''
      })));
    } else if (documentoEdit) {
      setDetalles([{ descripcion: '', cantidad: '', unidad: 'unidad', precio_unitario: '', presupuesto_costo_recurso: '', recurso_id: '' }]);
    }
  }, [documentoEdit]);

  const cargarProyectos = async () => {
    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/onebase');
      const response = await api.get(`${API_BASE_URL}/proyectos/`);
      setProyectos(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error al cargar proyectos:', error);
    }
  };

  const cargarProveedores = async () => {
    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/onebase');
      const response = await api.get(`${API_BASE_URL}/proveedores/`);
      setProveedores(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  const cargarClientes = async () => {
    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/onebase');
      const response = await api.get(`${API_BASE_URL}/clientes/`);
      setClientes(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  };

  const cargarRecursos = async () => {
    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/onebase');
      let allRecursos = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const response = await api.get(`${API_BASE_URL}/presupuestos-costo-recursos/`, { params: { page, status: 'activo' } });
        const data = response.data;
        if (Array.isArray(data)) {
          allRecursos = data;
          hasMore = false;
        } else if (data.results) {
          allRecursos = [...allRecursos, ...data.results];
          hasMore = !!data.next;
          page++;
        } else {
          hasMore = false;
        }
      }
      setRecursos(allRecursos);
    } catch (error) {
      console.error('Error al cargar recursos:', error);
    }
  };

  const cargarOrdenesCompra = async (proveedorId) => {
    if (!proveedorId) {
      setOrdenesCompra([]);
      return;
    }
    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/onebase');
      const response = await api.get(`${API_BASE_URL}/ordenes-compra/?proveedor=${proveedorId}`);
      const ordenes = response.data.results || response.data || [];
      setOrdenesCompra(ordenes);
    } catch (error) {
      console.error('Error al cargar ordenes de compra:', error);
      toast.error('Error al cargar las ordenes de compra');
    }
  };

  const cargarDetallesOrdenCompra = async (ordenCompraId) => {
    if (!ordenCompraId) return;
    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/onebase');
      const response = await api.get(`${API_BASE_URL}/ordenes-compra/${ordenCompraId}/`);
      const ordenCompra = response.data;
      setSelectedOrdenCompra(ordenCompra);

      if (ordenCompra.detalles && ordenCompra.detalles.length > 0) {
        const nuevosDetalles = ordenCompra.detalles.map(detalle => ({
          descripcion: detalle.descripcion || detalle.recurso_data?.nombre || '',
          cantidad: detalle.cantidad || '',
          unidad: detalle.unidad || 'unidad',
          precio_unitario: detalle.precio_unitario || '',
          presupuesto_costo_recurso: detalle.recurso_presupuesto_costo || '',
          recurso_id: ''
        }));
        setDetalles(nuevosDetalles);

        if (ordenCompra.proyecto && !formData.proyecto) {
          setFormData(prev => ({ ...prev, proyecto: ordenCompra.proyecto.toString() }));
        }
        toast.success(`${nuevosDetalles.length} items cargados desde la orden de compra`);
      }
    } catch (error) {
      console.error('Error al cargar detalles de orden de compra:', error);
      toast.error('Error al cargar los detalles de la orden de compra');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'tipo_documento') {
      if (value === 'factura_compra') {
        setFormData(prev => ({ ...prev, tipo_documento: value, cliente: '', orden_compra: '' }));
        setOrdenesCompra([]);
        setSelectedOrdenCompra(null);
      } else if (value === 'factura_venta') {
        setFormData(prev => ({ ...prev, tipo_documento: value, proveedor: '', orden_compra: '' }));
        setOrdenesCompra([]);
        setSelectedOrdenCompra(null);
      }
    }

    if (name === 'proveedor') {
      setFormData(prev => ({ ...prev, proveedor: value, orden_compra: '' }));
      setSelectedOrdenCompra(null);
      if (value) {
        cargarOrdenesCompra(value);
      } else {
        setOrdenesCompra([]);
      }
    }

    if (name === 'orden_compra') {
      if (value) {
        cargarDetallesOrdenCompra(value);
      } else {
        setSelectedOrdenCompra(null);
        setDetalles([{ descripcion: '', cantidad: '', unidad: 'unidad', precio_unitario: '', presupuesto_costo_recurso: '', recurso_id: '' }]);
      }
    }
  };

  const handleRecursoSelection = (index, presupuestoCostoRecursoId) => {
    const newDetalles = [...detalles];
    const pcr = recursos.find(r => r.id === parseInt(presupuestoCostoRecursoId));
    newDetalles[index] = {
      ...newDetalles[index],
      presupuesto_costo_recurso: presupuestoCostoRecursoId,
      recurso_id: pcr?.recurso_referencia ? String(pcr.recurso_referencia) : ''
    };
    if (pcr) {
      if (!newDetalles[index].descripcion) {
        newDetalles[index].descripcion = pcr.nombre;
      }
      const ref = pcr.recurso_referencia_data;
      if (ref?.unidad) {
        newDetalles[index].unidad = ref.unidad;
      }
    }
    setDetalles(newDetalles);
  };

  const handleDetalleChange = (index, field, value) => {
    const newDetalles = [...detalles];
    newDetalles[index] = { ...newDetalles[index], [field]: value };
    setDetalles(newDetalles);
  };

  const agregarDetalle = () => {
    setDetalles([...detalles, { descripcion: '', cantidad: '', unidad: 'unidad', precio_unitario: '', presupuesto_costo_recurso: '', recurso_id: '' }]);
  };

  const eliminarDetalle = (index) => {
    if (detalles.length > 1) {
      setDetalles(detalles.filter((_, i) => i !== index));
    } else {
      toast.warning('Debe haber al menos un detalle');
    }
  };

  const calcularSubtotal = (detalle) => {
    const cantidad = parseFloat(detalle.cantidad) || 0;
    const precio = parseFloat(detalle.precio_unitario) || 0;
    return cantidad * precio;
  };

  const calcularMontoNeto = () => {
    return detalles.reduce((sum, detalle) => sum + calcularSubtotal(detalle), 0);
  };

  const calcularIVA = () => {
    if (formData.tipo_documento === 'factura_exenta') return 0;
    return calcularMontoNeto() * 0.19;
  };

  const calcularTotal = () => {
    return calcularMontoNeto() + calcularIVA();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(value);
  };

  const validarFormulario = () => {
    if (!formData.tipo_documento) {
      toast.error('Debe seleccionar un tipo de documento');
      return false;
    }
    if (!formData.numero_documento) {
      toast.error('Debe ingresar el numero de documento');
      return false;
    }
    if (!formData.fecha_emision) {
      toast.error('Debe ingresar la fecha de emision');
      return false;
    }
    if (formData.tipo_documento === 'factura_compra' && !formData.proveedor) {
      toast.error('Debe seleccionar un proveedor para factura de compra');
      return false;
    }
    if (formData.tipo_documento === 'factura_venta' && !formData.cliente) {
      toast.error('Debe seleccionar un cliente para factura de venta');
      return false;
    }
    const detallesValidos = detalles.filter(d => d.descripcion && d.cantidad && d.precio_unitario);
    if (detallesValidos.length === 0) {
      toast.error('Debe agregar al menos un detalle completo');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validarFormulario()) return;

    setLoading(true);
    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/contabilidad');

      const detallesCompletos = detalles.filter(d => d.descripcion && d.cantidad && d.precio_unitario);

      const formDataObj = new FormData();
      formDataObj.append('tipo_documento', formData.tipo_documento);
      formDataObj.append('numero_documento', formData.numero_documento);
      formDataObj.append('descripcion', formData.descripcion || '');
      formDataObj.append('fecha_emision', formData.fecha_emision);
      if (formData.fecha_vencimiento) formDataObj.append('fecha_vencimiento', formData.fecha_vencimiento);
      if (formData.proyecto) formDataObj.append('proyecto', parseInt(formData.proyecto));
      if (formData.proveedor) formDataObj.append('proveedor', parseInt(formData.proveedor));
      if (formData.cliente) formDataObj.append('cliente', parseInt(formData.cliente));
      if (formData.orden_compra) formDataObj.append('orden_compra_origen', parseInt(formData.orden_compra));
      formDataObj.append('estado', formData.estado);
      formDataObj.append('monto_neto', calcularMontoNeto());
      formDataObj.append('monto_iva', calcularIVA());
      formDataObj.append('monto_total', calcularTotal());

      formDataObj.append('detalles', JSON.stringify(detallesCompletos.map(d => ({
        descripcion: d.descripcion,
        cantidad: parseFloat(d.cantidad),
        unidad: d.unidad,
        precio_unitario: parseFloat(d.precio_unitario),
        presupuesto_costo_recurso: d.presupuesto_costo_recurso ? parseInt(d.presupuesto_costo_recurso) : null
      }))));

      if (archivoAdjunto) formDataObj.append('documento_adjunto', archivoAdjunto);

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };

      if (documentoEdit) {
        await api.put(`${API_BASE_URL}/documentos-legales/${documentoEdit.id}/`, formDataObj, config);
        toast.success('Documento actualizado exitosamente');
      } else {
        await api.post(`${API_BASE_URL}/documentos-legales/`, formDataObj, config);
        toast.success('Documento creado exitosamente');
      }

      onCreate();
      onClose();
    } catch (error) {
      console.error('Error al guardar documento:', error);
      const errorMsg = error.response?.data?.numero_documento?.[0] ||
                      error.response?.data?.detail ||
                      'Error al guardar el documento';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const esFacturaCompra = formData.tipo_documento === 'factura_compra';
  const esFacturaVenta = formData.tipo_documento === 'factura_venta';
  const tipoInfo = TIPO_DOCUMENTO_CHOICES.find(t => t.value === formData.tipo_documento);
  const estadoInfo = ESTADO_CHOICES.find(e => e.value === formData.estado);

  const steps = [
    { label: 'Informacion', icon: <FiFileText /> },
    { label: 'Detalle Items', icon: <FiLayers /> },
    { label: 'Resumen', icon: <FiDollarSign /> },
  ];

  return (
    <div className="docleg-modal-overlay" onClick={onClose}>
      <div className="docleg-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="docleg-modal-header">
          <div className="docleg-modal-header-content">
            <div className="docleg-modal-header-icon">
              <FiFileText />
            </div>
            <div>
              <h2>{documentoEdit ? 'Editar' : 'Nuevo'} Documento Legal</h2>
              {tipoInfo && (
                <span className="docleg-modal-header-badge" style={{ background: tipoInfo.color }}>
                  {tipoInfo.icon} {tipoInfo.label}
                </span>
              )}
            </div>
          </div>
          <button className="docleg-modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>

        {/* Steps */}
        <div className="docleg-modal-steps">
          {steps.map((step, i) => (
            <button
              key={i}
              className={`docleg-step ${activeStep === i ? 'active' : ''} ${i < activeStep ? 'completed' : ''}`}
              onClick={() => setActiveStep(i)}
              type="button"
            >
              <span className="docleg-step-icon">{step.icon}</span>
              <span className="docleg-step-label">{step.label}</span>
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="docleg-modal-body">
            {/* Step 0: Informacion del Documento */}
            {activeStep === 0 && (
              <div className="docleg-modal-step-content">
                {/* Tipo de Documento - Visual Selector */}
                <div className="docleg-field-section">
                  <label className="docleg-field-label">
                    <FiFileText className="docleg-label-icon" />
                    Tipo de Documento *
                  </label>
                  <div className="docleg-tipo-grid">
                    {TIPO_DOCUMENTO_CHOICES.map(tipo => (
                      <button
                        key={tipo.value}
                        type="button"
                        className={`docleg-tipo-card ${formData.tipo_documento === tipo.value ? 'selected' : ''}`}
                        onClick={() => handleChange({ target: { name: 'tipo_documento', value: tipo.value } })}
                        style={{ '--tipo-color': tipo.color }}
                      >
                        <span className="docleg-tipo-emoji">{tipo.icon}</span>
                        <span className="docleg-tipo-name">{tipo.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Grid de campos principales */}
                <div className="docleg-fields-grid">
                  <div className="docleg-field">
                    <label className="docleg-field-label">
                      <FiHash className="docleg-label-icon" />
                      Numero Documento *
                    </label>
                    <input
                      type="text"
                      name="numero_documento"
                      value={formData.numero_documento}
                      onChange={handleChange}
                      placeholder="Ej: F-001-12345"
                      className="docleg-input"
                      required
                    />
                  </div>

                  <div className="docleg-field">
                    <label className="docleg-field-label">
                      <FiCalendar className="docleg-label-icon" />
                      Fecha Emision *
                    </label>
                    <input
                      type="date"
                      name="fecha_emision"
                      value={formData.fecha_emision}
                      onChange={handleChange}
                      className="docleg-input"
                      required
                    />
                  </div>

                  <div className="docleg-field">
                    <label className="docleg-field-label">
                      <FiCalendar className="docleg-label-icon" />
                      Fecha Vencimiento
                    </label>
                    <input
                      type="date"
                      name="fecha_vencimiento"
                      value={formData.fecha_vencimiento}
                      onChange={handleChange}
                      className="docleg-input"
                    />
                  </div>

                  <div className="docleg-field">
                    <label className="docleg-field-label">Estado *</label>
                    <div className="docleg-estado-selector">
                      {ESTADO_CHOICES.map(est => (
                        <button
                          key={est.value}
                          type="button"
                          className={`docleg-estado-chip ${formData.estado === est.value ? 'selected' : ''}`}
                          style={{ '--estado-color': est.color }}
                          onClick={() => setFormData(prev => ({ ...prev, estado: est.value }))}
                        >
                          <span className="docleg-estado-dot" style={{ background: est.color }} />
                          {est.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="docleg-field">
                    <label className="docleg-field-label">
                      <FiFolder className="docleg-label-icon" />
                      Proyecto
                    </label>
                    <select
                      name="proyecto"
                      value={formData.proyecto}
                      onChange={handleChange}
                      className="docleg-input"
                    >
                      <option value="">Sin proyecto</option>
                      {proyectos.map(p => (
                        <option key={p.id} value={p.id}>{p.nombre}</option>
                      ))}
                    </select>
                  </div>

                  {esFacturaCompra && (
                    <div className="docleg-field">
                      <label className="docleg-field-label">
                        <FiUser className="docleg-label-icon" />
                        Proveedor *
                      </label>
                      <select
                        name="proveedor"
                        value={formData.proveedor}
                        onChange={handleChange}
                        className="docleg-input"
                        required
                      >
                        <option value="">Seleccionar proveedor...</option>
                        {proveedores.map(p => (
                          <option key={p.id} value={p.id}>{p.nombre}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {esFacturaVenta && (
                    <div className="docleg-field">
                      <label className="docleg-field-label">
                        <FiUser className="docleg-label-icon" />
                        Cliente *
                      </label>
                      <select
                        name="cliente"
                        value={formData.cliente}
                        onChange={handleChange}
                        className="docleg-input"
                        required
                      >
                        <option value="">Seleccionar cliente...</option>
                        {clientes.map(c => (
                          <option key={c.id} value={c.id}>{c.nombre}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Orden de Compra */}
                {esFacturaCompra && formData.proveedor && ordenesCompra.length > 0 && (
                  <div className="docleg-field-section">
                    <label className="docleg-field-label">
                      <FiShoppingCart className="docleg-label-icon" />
                      Orden de Compra
                      <span className="docleg-label-hint">Seleccione para auto-llenar detalles</span>
                    </label>
                    <select
                      name="orden_compra"
                      value={formData.orden_compra}
                      onChange={handleChange}
                      className="docleg-input docleg-input-highlight"
                    >
                      <option value="">Ingresar detalles manualmente...</option>
                      {ordenesCompra.map(oc => (
                        <option key={oc.id} value={oc.id}>
                          {oc.numero_oc} - {oc.descripcion || 'Sin descripcion'} ({formatCurrency(oc.total_oc || 0)})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {esFacturaCompra && formData.proveedor && ordenesCompra.length === 0 && (
                  <div className="docleg-info-banner">
                    <span>ℹ️</span>
                    No hay ordenes de compra disponibles para este proveedor
                  </div>
                )}

                {/* Descripcion */}
                <div className="docleg-field-section">
                  <label className="docleg-field-label">
                    <FiAlignLeft className="docleg-label-icon" />
                    Descripcion
                  </label>
                  <textarea
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Descripcion general del documento..."
                    className="docleg-input docleg-textarea"
                  />
                </div>

                {/* Archivo Adjunto */}
                <div className="docleg-field-section">
                  <label className="docleg-field-label">
                    <FiPaperclip className="docleg-label-icon" />
                    Documento Adjunto
                  </label>
                  <div className={`docleg-file-upload ${archivoAdjunto ? 'has-file' : ''}`}>
                    {documentoEdit?.documento_adjunto && !archivoAdjunto && (
                      <div className="docleg-file-current">
                        <FiPaperclip />
                        <a href={documentoEdit.documento_adjunto} target="_blank" rel="noopener noreferrer">
                          Ver documento actual
                        </a>
                      </div>
                    )}
                    <div className="docleg-file-input-row">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.xml,.doc,.docx"
                        onChange={(e) => setArchivoAdjunto(e.target.files[0] || null)}
                      />
                      {archivoAdjunto && (
                        <button
                          type="button"
                          className="docleg-file-remove"
                          onClick={() => {
                            setArchivoAdjunto(null);
                            const fileInput = document.querySelector('.docleg-file-upload input[type="file"]');
                            if (fileInput) fileInput.value = '';
                          }}
                        >
                          <FiX />
                        </button>
                      )}
                    </div>
                    {archivoAdjunto && (
                      <div className="docleg-file-name">
                        <FiPaperclip /> {archivoAdjunto.name}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Detalles del Documento */}
            {activeStep === 1 && (
              <div className="docleg-modal-step-content">
                <div className="docleg-detalles-header">
                  <div>
                    <h3>Items del Documento</h3>
                    <p>{detalles.length} item{detalles.length !== 1 ? 's' : ''} agregado{detalles.length !== 1 ? 's' : ''}</p>
                  </div>
                  <button
                    type="button"
                    className="docleg-btn-add"
                    onClick={agregarDetalle}
                  >
                    <FiPlus /> Agregar Item
                  </button>
                </div>

                <div className="docleg-detalles-list">
                  {detalles.map((detalle, index) => {
                    const pcrSeleccionado = detalle.presupuesto_costo_recurso ? recursos.find(r => r.id === parseInt(detalle.presupuesto_costo_recurso)) : null;
                    return (
                      <div key={index} className="docleg-detalle-card">
                        <div className="docleg-detalle-top">
                          <span className="docleg-detalle-num">#{index + 1}</span>
                          <span className="docleg-detalle-subtotal">
                            {formatCurrency(calcularSubtotal(detalle))}
                          </span>
                          {detalles.length > 1 && (
                            <button
                              type="button"
                              className="docleg-detalle-remove"
                              onClick={() => eliminarDetalle(index)}
                            >
                              <FiTrash2 />
                            </button>
                          )}
                        </div>

                        {/* Recurso selector */}
                        <div className="docleg-detalle-recurso">
                          <label>
                            <FiPackage className="docleg-label-icon" />
                            Recurso Presupuesto
                          </label>
                          <div className="docleg-detalle-recurso-row">
                            <select
                              value={detalle.presupuesto_costo_recurso || ''}
                              onChange={(e) => handleRecursoSelection(index, e.target.value)}
                              className="docleg-input"
                            >
                              <option value="">Seleccionar recurso...</option>
                              {recursos.map(r => (
                                <option key={r.id} value={r.id}>
                                  {r.nombre}{r.recurso_referencia_data?.tipo_recurso ? ` (${r.recurso_referencia_data.tipo_recurso.nombre})` : ''}
                                </option>
                              ))}
                            </select>
                            {pcrSeleccionado && (
                              <div className="docleg-recurso-badges">
                                {pcrSeleccionado.recurso_referencia_data?.unidad && (
                                  <span className="docleg-recurso-badge">{pcrSeleccionado.recurso_referencia_data.unidad}</span>
                                )}
                                {pcrSeleccionado.precio_unitario_costo > 0 && (
                                  <span className="docleg-recurso-badge">Costo: {formatCurrency(pcrSeleccionado.precio_unitario_costo)}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Campos del detalle */}
                        <div className="docleg-detalle-fields">
                          <div className="docleg-detalle-desc">
                            <label>Descripcion *</label>
                            <input
                              type="text"
                              value={detalle.descripcion}
                              onChange={(e) => handleDetalleChange(index, 'descripcion', e.target.value)}
                              placeholder="Descripcion del item..."
                              className="docleg-input"
                              required
                            />
                          </div>
                          <div className="docleg-detalle-num-fields">
                            <div>
                              <label>Cantidad *</label>
                              <input
                                type="number"
                                value={detalle.cantidad}
                                onChange={(e) => handleDetalleChange(index, 'cantidad', e.target.value)}
                                min="0"
                                step="0.01"
                                placeholder="0"
                                className="docleg-input"
                                required
                              />
                            </div>
                            <div>
                              <label>Unidad *</label>
                              <input
                                type="text"
                                value={detalle.unidad}
                                onChange={(e) => handleDetalleChange(index, 'unidad', e.target.value)}
                                placeholder="unidad"
                                className="docleg-input"
                                required
                              />
                            </div>
                            <div>
                              <label>Precio Unitario *</label>
                              <input
                                type="number"
                                value={detalle.precio_unitario}
                                onChange={(e) => handleDetalleChange(index, 'precio_unitario', e.target.value)}
                                min="0"
                                step="0.01"
                                placeholder="0"
                                className="docleg-input"
                                required
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 2: Resumen / Totales */}
            {activeStep === 2 && (
              <div className="docleg-modal-step-content">
                {/* Resumen de informacion */}
                <div className="docleg-resumen-card">
                  <h3>Resumen del Documento</h3>
                  <div className="docleg-resumen-grid">
                    <div className="docleg-resumen-item">
                      <span className="docleg-resumen-label">Tipo</span>
                      <span className="docleg-resumen-value">
                        {tipoInfo ? `${tipoInfo.icon} ${tipoInfo.label}` : '-'}
                      </span>
                    </div>
                    <div className="docleg-resumen-item">
                      <span className="docleg-resumen-label">Numero</span>
                      <span className="docleg-resumen-value">{formData.numero_documento || '-'}</span>
                    </div>
                    <div className="docleg-resumen-item">
                      <span className="docleg-resumen-label">Emision</span>
                      <span className="docleg-resumen-value">{formData.fecha_emision || '-'}</span>
                    </div>
                    <div className="docleg-resumen-item">
                      <span className="docleg-resumen-label">Estado</span>
                      <span className="docleg-resumen-value" style={{ color: estadoInfo?.color }}>
                        {estadoInfo?.label || '-'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Tabla de items */}
                {detalles.filter(d => d.descripcion).length > 0 && (
                  <div className="docleg-resumen-card">
                    <h3>Items ({detalles.filter(d => d.descripcion).length})</h3>
                    <table className="docleg-resumen-table">
                      <thead>
                        <tr>
                          <th>Descripcion</th>
                          <th>Cant.</th>
                          <th>P. Unit.</th>
                          <th>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detalles.filter(d => d.descripcion).map((d, i) => (
                          <tr key={i}>
                            <td>{d.descripcion}</td>
                            <td>{d.cantidad} {d.unidad}</td>
                            <td>{formatCurrency(parseFloat(d.precio_unitario) || 0)}</td>
                            <td>{formatCurrency(calcularSubtotal(d))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Totales */}
                <div className="docleg-totales-card">
                  <div className="docleg-total-row">
                    <span>Monto Neto</span>
                    <span>{formatCurrency(calcularMontoNeto())}</span>
                  </div>
                  <div className="docleg-total-row">
                    <span>IVA (19%)</span>
                    <span>{formatCurrency(calcularIVA())}</span>
                  </div>
                  <div className="docleg-total-row docleg-total-final">
                    <span>Total</span>
                    <span>{formatCurrency(calcularTotal())}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="docleg-modal-footer">
            <button type="button" className="docleg-btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <div className="docleg-footer-right">
              {activeStep > 0 && (
                <button
                  type="button"
                  className="docleg-btn-secondary"
                  onClick={() => setActiveStep(prev => prev - 1)}
                >
                  Anterior
                </button>
              )}
              {activeStep < 2 ? (
                <button
                  type="button"
                  className="docleg-btn-primary"
                  onClick={() => setActiveStep(prev => prev + 1)}
                >
                  Siguiente
                </button>
              ) : (
                <button
                  type="submit"
                  className="docleg-btn-submit"
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : documentoEdit ? 'Actualizar Documento' : 'Crear Documento'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrearEditarDocumentoLegalModal;
